import { useCallback, useEffect, useRef, useState } from 'react';
import { imageCache } from '../utils/imageCache';

interface SignedImageUrl {
  url: string | null;
  failed: boolean;
  loading: boolean;
  onError: () => Promise<void>;
  onLoad: () => void;
}

// Signed URL for an S3 image path; on <img> error re-signs once, then reports failed (stale results for an old path are dropped)
export const useSignedImageUrl = (imagePath?: string | null): SignedImageUrl => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const pathRef = useRef(imagePath);
  const retriedRef = useRef(false);

  useEffect(() => {
    pathRef.current = imagePath;
    retriedRef.current = false;
    setFailed(false);
    setUrl(null);
    if (!imagePath) return;
    imageCache.getSignedUrl(imagePath).then((signed) => {
      if (pathRef.current !== imagePath) return;
      if (signed) setUrl(signed);
      else setFailed(true);
    }).catch(() => {
      if (pathRef.current === imagePath) setFailed(true);
    });
  }, [imagePath]);

  const onError = useCallback(async () => {
    const path = pathRef.current;
    // Clear the dead URL straight away so the loading state shows, not a broken-image icon
    setUrl(null);
    if (!path || retriedRef.current) {
      setFailed(true);
      return;
    }
    retriedRef.current = true;
    let fresh: string | null = null;
    try {
      fresh = await imageCache.refreshSignedUrl(path);
    } catch (error) {
      console.error("Error re-signing image URL:", error);
    }
    if (pathRef.current !== path) return;
    if (fresh) setUrl(fresh);
    else setFailed(true);
  }, []);

  // Successful load re-arms the retry so a later expiry on the same path can recover too
  const onLoad = useCallback(() => {
    retriedRef.current = false;
  }, []);

  const loading = !!imagePath && !url && !failed;

  return { url, failed, loading, onError, onLoad };
};
